/**
 * @license Copyright (c) 2003-2022, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

/**
 * @module image/imageupload/imageuploadui
 */

import { Plugin, icons } from 'ckeditor5/src/core';
import { FileDialogButtonView } from 'ckeditor5/src/upload';
import { ButtonView } from 'ckeditor5/src/ui';

function createImageTypeRegExp( types ) {
    // Sanitize the MIME type name which may include: "+", "-" or ".".
    const regExpSafeNames = types.map( type => type.replace( '+', '\\+' ) );

    return new RegExp( `^image\\/(${ regExpSafeNames.join( '|' ) })$` );
}


/**
 * The image upload button plugin.
 *
 * For a detailed overview, check the {@glink features/images/image-upload/image-upload Image upload feature} documentation.
 *
 * Adds the `'uploadImage'` button to the {@link module:ui/componentfactory~ComponentFactory UI component factory}
 * and also the `imageUpload` button as an alias for backward compatibility.
 *
 * @extends module:core/plugin~Plugin
 */
export default class SbImageUploadUI extends Plugin {
    /**
     * @inheritDoc
     */
    static get pluginName() {
        return 'SBImageUploadUI';
    }

    /**
     * @inheritDoc
     */
    init() {

        const editor = this.editor;
        const t = editor.t;
        this.bind('openSelectFileModal').to(editor, 'sbOpenSelectFileModal');

        const componentCreator = locale => {
            const view = new ButtonView( locale );
            const command = editor.commands.get( 'uploadImage' );
            const imageTypes = editor.config.get( 'image.upload.types' );
            const imageTypesRegExp = createImageTypeRegExp( imageTypes );

            view.set( {
                label: t( 'Insert image' ),
                icon: icons.image,
                tooltip: true
            } );

            view.bind( 'isEnabled' ).to( command );

            this.listenTo( view, 'execute', () => {
                this.openSelectFileModal().then(async (files) => {
                    if(!files) {
                        return;
                    }

                    let imagesToUpload = [];
                    // SmartDocs files come back as an array
                    if(Array.isArray(files)) {
                        for(const item of files) {
                            const d = await fetch(item.previewUrlUnwrapped);
                            const blob = await d.blob();
                            const filename = item.file_name;
                            const file = new File([blob], filename);
                            imagesToUpload.push(file);

                            console.log(blob);
                        }
                    }else{
                        imagesToUpload = Array.from( [files] )
                                                              .filter( file => imageTypesRegExp.test( file.type ) );
                    }

                    console.log(imagesToUpload);

                    if ( imagesToUpload.length ) {
                        editor.execute( 'uploadImage', { file: imagesToUpload } );
                        editor.editing.view.focus();
                    }
                });
            } );

            view.on( 'done', ( evt, files ) => {
                const imagesToUpload = Array.from( files ).filter( file => imageTypesRegExp.test( file.type ) );

                if ( imagesToUpload.length ) {
                    editor.execute( 'uploadImage', { file: imagesToUpload } );

                    editor.editing.view.focus();
                }
            } );

            return view;
        };
        
        editor.ui.componentFactory.add( 'sbUploadImage', componentCreator );
    }
}
